import {
    Drawer,
    DrawerBody,
    DrawerOverlay,
    DrawerContent,
    Box,
    Text,
    HStack,
    Alert,
    AlertIcon,
    AlertDescription,
    Flex,
    IconButton,
    CloseButton,
    Grid,
    GridItem,
    Input,
    Icon,
    Collapse,
    useDisclosure,
    useToast,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    Spinner,
    Center,
    Badge,
    Tooltip,
    Link as ChakraLink,
} from '@chakra-ui/react';
import React, { useState, useEffect, useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { TbPencil, TbChevronDown, TbChevronUp, TbCopy, TbMessageCircle, TbTruck } from 'react-icons/tb';
import { FiFileText, FiDollarSign, FiShoppingCart, FiPackage, FiRefreshCw } from 'react-icons/fi';
import { useGetOrderDrawer } from '../hooks/useGetOrderDrawer';
import { useForceSidebarCollapsed } from '../contexts/SidebarCollapseOverrideContext';
import { useUpdateOrderDetails } from '../hooks/useUpdateOrderDetails';
import { formatPrice } from '../functions';
import GrossMarginSummaryValue from '../components/GrossMarginSummaryValue';
import StatusIndicator from '../components/StatusIndicator';
import ActionBadge from '../components/ActionBadge';
import OrderTag from '../components/OrderTag';
import SessionContext from '../contexts/SessionContext';
import { OrderChatPanel } from './OrderChatDrawer';
import {
    EmsTabPanel,
    QuotingTabPanel,
    SelectionTabPanel,
    OrderedTabPanel,
    RekeyTabPanel
} from '../components/orderDrawer';
import SupplierNotesModal, { SupplierOrderSummary } from './SupplierNotesModal';
import useCopyToClipboard from '../hooks/useCopyToClipboard';
import CopyTooltip from '../components/CopyTooltip';

interface OrderDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    orderId: string | null;
    openToNotes?: boolean;
    initialPanelState?: OrderDrawerPanelState;
    onPanelStateChange?: (state: OrderDrawerPanelState) => void;
    disableEnterAnimation?: boolean;
    /** Deep-link from the Delivery Board: expand this supplier order and highlight this part. */
    clickedSupplierOrderId?: string;
    clickedOrderPartId?: string;
}

export interface OrderDrawerPanelState {
    panel: 'none' | 'chat' | 'supplier';
    supplierInitialTab: 'quoting-comments' | 'order-messages';
    supplierInitialSupplierOrderId: string | null;
    workflowTab: OrderDrawerWorkflowTab;
}

type OrderDrawerWorkflowTab = 'ems' | 'quoting' | 'selection' | 'ordered' | 'rekey';
const ORDER_DRAWER_WORKFLOW_TABS: OrderDrawerWorkflowTab[] = ['ems', 'quoting', 'selection', 'ordered', 'rekey'];
const ORDER_DRAWER_WORKFLOW_TO_ORDER_STATUS: Record<OrderDrawerWorkflowTab, 'newParts' | 'quoting' | 'selection' | 'ordered' | 'rekey'> = {
    ems: 'newParts',
    quoting: 'quoting',
    selection: 'selection',
    ordered: 'ordered',
    rekey: 'rekey',
};

const OrderDrawer = ({
    isOpen,
    onClose,
    orderId,
    openToNotes = false,
    initialPanelState,
    onPanelStateChange,
    disableEnterAnimation = false,
    clickedSupplierOrderId,
    clickedOrderPartId,
}: OrderDrawerProps) => {
    useForceSidebarCollapsed(isOpen);
    const resolvedInitialPanelState: OrderDrawerPanelState = React.useMemo(() => ({
        panel: initialPanelState?.panel ?? (openToNotes ? 'chat' : 'none'),
        supplierInitialTab: initialPanelState?.supplierInitialTab ?? 'quoting-comments',
        supplierInitialSupplierOrderId: initialPanelState?.supplierInitialSupplierOrderId ?? null,
        workflowTab: initialPanelState?.workflowTab ?? 'ems',
    }), [
        initialPanelState?.panel,
        initialPanelState?.supplierInitialTab,
        initialPanelState?.supplierInitialSupplierOrderId,
        initialPanelState?.workflowTab,
        openToNotes,
    ]);

    const { data, isLoading, isError, refetch } = useGetOrderDrawer(orderId, isOpen);
    const updateOrderDetails = useUpdateOrderDetails();
    const sessionContext = useContext(SessionContext);
    const [isEditingRONumber, setIsEditingRONumber] = useState(false);
    const [roNumberValue, setRONumberValue] = useState('');
    const { isOpen: isDetailsOpen, onToggle: onDetailsToggle } = useDisclosure();
    const toast = useToast();

    // Chat drawer state (replaces inline notes panel)
    const [isChatOpen, setIsChatOpen] = useState(() => resolvedInitialPanelState.panel === 'chat');
    const [chatComposeMode, setChatComposeMode] = useState<'message' | 'note'>('message');
    const [isSupplierNotesOpen, setIsSupplierNotesOpen] = useState(() => resolvedInitialPanelState.panel === 'supplier');
    const [supplierNotesInitialTab, setSupplierNotesInitialTab] = useState<'quoting-comments' | 'order-messages'>(() => resolvedInitialPanelState.supplierInitialTab);
    const [supplierNotesInitialSupplierOrderId, setSupplierNotesInitialSupplierOrderId] = useState<string | null>(() => resolvedInitialPanelState.supplierInitialSupplierOrderId);
    const [activeWorkflowTab, setActiveWorkflowTab] = useState<OrderDrawerWorkflowTab>(() => resolvedInitialPanelState.workflowTab);
    const initialPanelStateRef = React.useRef<OrderDrawerPanelState>(resolvedInitialPanelState);
    const requestedPanelStateRef = React.useRef<OrderDrawerPanelState>(resolvedInitialPanelState);
    initialPanelStateRef.current = resolvedInitialPanelState;

    const noteCount = data?.notes?.length ?? 0;
    const supplierCommunicationCount = React.useMemo(() => {
        const notesCount = data?.supplierNotes?.length ?? 0;
        const messageCounts = data?.supplierMessageCounts ?? {};
        return notesCount + Object.values(messageCounts).reduce((sum, count) => sum + count, 0);
    }, [data?.supplierMessageCounts, data?.supplierNotes]);
    const hasEms = !!data?.ems;
    const hasQuotes = !!(data?.quotes && data.quotes.quotes.length > 0);
    const hasSelection = !!data?.selection;
    const hasOrdered = !!data?.ordered;
    const hasRekey = !!data?.rekey;
    const availableWorkflowTabs = React.useMemo<OrderDrawerWorkflowTab[]>(() => (
        ORDER_DRAWER_WORKFLOW_TABS.filter((tab) => {
            if (tab === 'ems') return hasEms;
            if (tab === 'quoting') return hasQuotes;
            if (tab === 'selection') return hasSelection;
            if (tab === 'ordered') return hasOrdered;
            if (tab === 'rekey') return hasRekey;
            return false;
        })
    ), [hasEms, hasQuotes, hasSelection, hasOrdered, hasRekey]);
    const hasCurrentUserReviewed = !!(sessionContext?.isAuthenticated && data?.reviewedByUsers?.some(
        (reviewer) => reviewer._id === sessionContext.userId
    ));

    const supplierOrderSummaries = React.useMemo<Array<SupplierOrderSummary>>(() => {
        if (!data?.ordered) return [];

        const dedupedOrders = new Map<string, SupplierOrderSummary>();
        const orderedBuckets = [
            data.ordered.ordered?.orders ?? [],
            data.ordered.confirmed?.orders ?? [],
            data.ordered.cancelled?.orders ?? [],
            data.ordered.rejected?.orders ?? [],
        ];

        orderedBuckets.flat().forEach((supplierOrder) => {
            if (supplierOrder.source !== 'partstrader' && supplierOrder.source !== 'opstrax' && supplierOrder.source !== 'ccc') return;
            if (dedupedOrders.has(supplierOrder.supplierOrderId)) return;

            const supplierStatus = supplierOrder.status;
            if (!['ordered', 'confirmed', 'rejected', 'cancelled'].includes(supplierStatus)) return;

            dedupedOrders.set(supplierOrder.supplierOrderId, {
                supplierOrderId: supplierOrder.supplierOrderId,
                supplierName: supplierOrder.title,
                status: supplierStatus as SupplierOrderSummary['status'],
                phoneNumber: supplierOrder.supplierPhone,
                email: supplierOrder.supplierEmail,
                source: supplierOrder.source,
                displayId: supplierOrder.displayId,
            });
        });

        return Array.from(dedupedOrders.values());
    }, [data?.ordered]);

    const openSupplierCommunications = (options?: { initialTab?: 'quoting-comments' | 'order-messages'; supplierOrderId?: string; }) => {
        setSupplierNotesInitialTab(options?.initialTab ?? 'quoting-comments');
        setSupplierNotesInitialSupplierOrderId(options?.supplierOrderId ?? null);
        setIsChatOpen(false);
        setIsSupplierNotesOpen(true);
    };

    const isHydratingPanelStateRef = React.useRef(false);

    // Initialize side panel state for the currently opened order.
    useEffect(() => {
        if (isOpen) {
            const initialPanelStateValue = initialPanelStateRef.current;
            const requestedWorkflowTab = initialPanelStateValue?.workflowTab ?? 'ems';
            const normalizedWorkflowTab = availableWorkflowTabs.length > 0 && !availableWorkflowTabs.includes(requestedWorkflowTab)
                ? availableWorkflowTabs[0]
                : requestedWorkflowTab;
            const requestedPanelState: OrderDrawerPanelState = {
                panel: initialPanelStateValue?.panel ?? 'none',
                supplierInitialTab: initialPanelStateValue?.supplierInitialTab ?? 'quoting-comments',
                supplierInitialSupplierOrderId: initialPanelStateValue?.supplierInitialSupplierOrderId ?? null,
                workflowTab: normalizedWorkflowTab,
            };

            requestedPanelStateRef.current = requestedPanelState;
            isHydratingPanelStateRef.current = true;
            setSupplierNotesInitialTab(requestedPanelState.supplierInitialTab);
            setSupplierNotesInitialSupplierOrderId(requestedPanelState.supplierInitialSupplierOrderId);
            setIsChatOpen(requestedPanelState.panel === 'chat');
            setIsSupplierNotesOpen(requestedPanelState.panel === 'supplier');
            setActiveWorkflowTab(requestedPanelState.workflowTab);
            return;
        }

        isHydratingPanelStateRef.current = false;
    }, [isOpen, orderId, availableWorkflowTabs]);

    // Open chat drawer when opened via "Send Message" / "Add Note"
    useEffect(() => {
        if (openToNotes && isOpen) {
            setIsSupplierNotesOpen(false);
            setIsChatOpen(true);
        }
    }, [openToNotes, isOpen]);

    useEffect(() => {
        if (!onPanelStateChange || !isOpen) return;

        const currentState: OrderDrawerPanelState = {
            panel: isSupplierNotesOpen ? 'supplier' : isChatOpen ? 'chat' : 'none',
            supplierInitialTab: supplierNotesInitialTab,
            supplierInitialSupplierOrderId: supplierNotesInitialSupplierOrderId,
            workflowTab: activeWorkflowTab,
        };

        const targetState = requestedPanelStateRef.current;

        if (isHydratingPanelStateRef.current) {
            const isHydrated =
                currentState.panel === targetState.panel
                && currentState.supplierInitialTab === targetState.supplierInitialTab
                && currentState.supplierInitialSupplierOrderId === targetState.supplierInitialSupplierOrderId
                && currentState.workflowTab === targetState.workflowTab;

            if (isHydrated) {
                isHydratingPanelStateRef.current = false;
            }

            return;
        }

        onPanelStateChange(currentState);
    }, [
        onPanelStateChange,
        isOpen,
        isSupplierNotesOpen,
        isChatOpen,
        supplierNotesInitialTab,
        supplierNotesInitialSupplierOrderId,
        activeWorkflowTab,
    ]);

    const handleStartEditingRONumber = () => {
        setRONumberValue(data?.roNumber || '');
        setIsEditingRONumber(true);
    };

    const handleSaveRONumber = async () => {
        if (!orderId || roNumberValue === data?.roNumber) {
            setIsEditingRONumber(false);
            return;
        }

        try {
            await updateOrderDetails.mutateAsync({
                orderId,
                roNumber: roNumberValue,
            });
        } catch {
        } finally {
            setIsEditingRONumber(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSaveRONumber();
        } else if (e.key === 'Escape') {
            setRONumberValue(data?.roNumber || '');
            setIsEditingRONumber(false);
        }
    };

    const { copy: copyText, isCopied } = useCopyToClipboard();

    const copyToClipboard = async (text: string, key: string = 'vin', e?: React.MouseEvent) => {
        const ok = await copyText(text, key, e ? { x: e.clientX, y: e.clientY } : null);
        if (!ok) {
            toast({
                title: 'Copy Failed',
                description: 'Failed to copy to clipboard',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    // Segmented selector (ENG-2150): white active segment on a grey track — no
    // blue fill. Same geometry and tokens as the SegmentedControl primitive.
    const tabStyles = {
        borderRadius: "md",
        fontWeight: "medium",
        fontSize: "sm",
        color: "fg.muted",
        bg: "transparent",
        border: "1px solid",
        borderColor: "transparent",
        flex: 1,
        py: 1,
        px: 3,
        _selected: {
            color: "fg.default",
            bg: "bg.surface",
            borderColor: "border.default",
            fontWeight: "semibold",
        },
        _hover: {
            color: "fg.default",
        },
        _disabled: {
            opacity: 0.4,
            cursor: "not-allowed",
            color: "fg.subtle",
            _hover: {
                color: "fg.subtle",
            }
        }
    };

    const TabBadge = ({ count }: { count: number; }) => (
        <Flex
            ml={1.5}
            bg="bg.muted"
            borderRadius="full"
            h="18px"
            minW="18px"
            px="5px"
            align="center"
            justify="center"
            flexShrink={0}
            sx={{
                '.chakra-tabs__tab[aria-selected=true] &': {
                    bg: 'bg.subtle',
                }
            }}
        >
            <Text
                fontSize="9px"
                fontWeight="bold"
                lineHeight="1"
                color="fg.muted"
            >
                {count}
            </Text>
        </Flex>
    );

    useEffect(() => {
        if (!isOpen || availableWorkflowTabs.length === 0) return;

        const normalizedWorkflowTab = availableWorkflowTabs.includes(activeWorkflowTab)
            ? activeWorkflowTab
            : availableWorkflowTabs[0];

        if (!availableWorkflowTabs.includes(requestedPanelStateRef.current.workflowTab)) {
            requestedPanelStateRef.current = {
                ...requestedPanelStateRef.current,
                workflowTab: normalizedWorkflowTab,
            };
        }

        if (activeWorkflowTab !== normalizedWorkflowTab) {
            setActiveWorkflowTab(normalizedWorkflowTab);
        }
    }, [isOpen, activeWorkflowTab, availableWorkflowTabs]);

    return (
        <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="full">
            <DrawerOverlay
                motionProps={disableEnterAnimation ? { initial: false, transition: { duration: 0 } } : undefined}
            />
            <DrawerContent
                data-partbay-drawer=""
                motionProps={disableEnterAnimation ? { initial: false, transition: { duration: 0 } } : undefined}
                overflow="hidden"
                maxW={(isChatOpen || isSupplierNotesOpen) ? "1300px" : "920px"}
                transition="max-width 0.2s ease"
            >

                {isLoading ? (
                    <DrawerBody>
                        <Center h="100%">
                            <Spinner size="xl" color="blue.500" />
                        </Center>
                    </DrawerBody>
                ) : isError ? (
                    <DrawerBody>
                        <Alert status="error" mt={4}>
                            <AlertIcon />
                            Failed to load order details.
                        </Alert>
                    </DrawerBody>
                ) : data ? (
                    <>
                        <Flex h="100%">
                            {/* Main Content */}
                            <Box flex="1" display="flex" flexDirection="column" overflow="hidden">
                                {/* Top Header - Grey */}
                                <Box px={6} py={4} bg="bg.subtle" borderBottomWidth="1px" borderColor="border.default" overflow="visible">
                                    <Flex align="center" justify="space-between">
                                        <HStack spacing={4} align="center">
                                            <ChakraLink
                                                as={RouterLink}
                                                to={orderId ? `/order/${orderId}` : '/'}
                                                state={{
                                                    source: 'home',
                                                    initialStatus: ORDER_DRAWER_WORKFLOW_TO_ORDER_STATUS[activeWorkflowTab],
                                                    homeReturnState: {
                                                        selectedOrderId: orderId,
                                                        isOrderDrawerOpen: Boolean(orderId),
                                                        panelState: {
                                                            panel: isSupplierNotesOpen ? 'supplier' : isChatOpen ? 'chat' : 'none',
                                                            supplierInitialTab: supplierNotesInitialTab,
                                                            supplierInitialSupplierOrderId: supplierNotesInitialSupplierOrderId,
                                                            workflowTab: activeWorkflowTab,
                                                        },
                                                    },
                                                }}
                                                fontSize="lg"
                                                fontWeight="bold"
                                                color="#0071E3"
                                                _hover={{ textDecoration: "underline" }}
                                            >
                                                {data.vehicle}
                                            </ChakraLink>

                                            <Box h="24px" w="1px" bg="gray.300" />

                                            <HStack spacing={2}>
                                                <Text fontSize="lg" fontWeight="bold" color="gray.900">RO</Text>
                                                {isEditingRONumber ? (
                                                    <Input
                                                        value={roNumberValue}
                                                        onChange={(e) => setRONumberValue(e.target.value)}
                                                        onBlur={handleSaveRONumber}
                                                        onKeyDown={handleKeyPress}
                                                        size="sm"
                                                        width="120px"
                                                        autoFocus
                                                        isDisabled={updateOrderDetails.isPending}
                                                        fontSize="xl"
                                                        fontWeight="bold"
                                                        color="gray.900"
                                                    />
                                                ) : (
                                                    <HStack spacing={2}>
                                                        <Text fontSize="lg" fontWeight="bold" color="gray.900">
                                                            {data.roNumber}
                                                        </Text>
                                                        <IconButton
                                                            aria-label="Edit RO Number"
                                                            icon={<Icon as={TbPencil as React.ElementType} w={4} h={4} />}
                                                            size="xs"
                                                            variant="ghost"
                                                            onClick={handleStartEditingRONumber}
                                                            isDisabled={updateOrderDetails.isPending}
                                                            _hover={{
                                                                bg: "gray.200"
                                                            }}
                                                            minW="auto"
                                                            h="auto"
                                                            p={1}
                                                        />
                                                    </HStack>
                                                )}
                                            </HStack>

                                            <Box h="24px" w="1px" bg="gray.300" />

                                            <StatusIndicator
                                                orderId={orderId!}
                                                completed={data.completed}
                                                manuallyFlagged={data.manuallyFlagged}
                                                archived={data.archived}
                                                showLabel={true}
                                                iconSize="12px"
                                                reviewedByCurrentUser={hasCurrentUserReviewed}
                                                onToggleReview={(id, reviewed) => {
                                                    updateOrderDetails.mutate({ orderId: id, reviewedByMe: reviewed });
                                                }}
                                                onOpenChat={(_orderId, mode) => {
                                                    setChatComposeMode(mode);
                                                    setIsSupplierNotesOpen(false);
                                                    setIsChatOpen(true);
                                                }}
                                            />

                                        </HStack>
                                        <HStack spacing={1}>
                                            {/* Supplier Communications Toggle */}
                                            {(supplierCommunicationCount > 0 || supplierOrderSummaries.length > 0) && (
                                                <Tooltip label="Supplier Communications" placement="bottom">
                                                    <Box position="relative" cursor="pointer" onClick={() => {
                                                        if (isSupplierNotesOpen) {
                                                            setIsSupplierNotesOpen(false);
                                                        } else {
                                                            openSupplierCommunications();
                                                        }
                                                    }}>
                                                        <IconButton
                                                            aria-label="Toggle supplier communications"
                                                            icon={<Icon as={TbTruck as React.ElementType} w={5} h={5} />}
                                                            size="sm"
                                                            variant="ghost"
                                                            color={isSupplierNotesOpen ? "orange.500" : "gray.500"}
                                                            bg={isSupplierNotesOpen ? "orange.50" : "transparent"}
                                                            _hover={{ bg: isSupplierNotesOpen ? "orange.100" : "gray.200" }}
                                                            minW="auto"
                                                            h="auto"
                                                            p={1.5}
                                                            borderRadius="md"
                                                        />
                                                        {supplierCommunicationCount > 0 && (
                                                            <Flex
                                                                position="absolute"
                                                                top="-4px"
                                                                right="-6px"
                                                                bg="orange.500"
                                                                color="white"
                                                                borderRadius="full"
                                                                minW="16px"
                                                                h="16px"
                                                                px="4px"
                                                                align="center"
                                                                justify="center"
                                                                fontSize="9px"
                                                                fontWeight="bold"
                                                                lineHeight="1"
                                                                pointerEvents="none"
                                                            >
                                                                {supplierCommunicationCount}
                                                            </Flex>
                                                        )}
                                                    </Box>
                                                </Tooltip>
                                            )}
                                            {/* Chat Toggle */}
                                            <Box position="relative" cursor="pointer" onClick={() => {
                                                setIsChatOpen(prev => {
                                                    const next = !prev;
                                                    if (next) setIsSupplierNotesOpen(false);
                                                    return next;
                                                });
                                            }}>
                                                <IconButton
                                                    aria-label="Toggle chat"
                                                    icon={<Icon as={TbMessageCircle as React.ElementType} w={5} h={5} />}
                                                    size="sm"
                                                    variant="ghost"
                                                    color={isChatOpen ? "#0071E3" : "gray.500"}
                                                    bg={isChatOpen ? "#0071E315" : "transparent"}
                                                    _hover={{ bg: isChatOpen ? "#0071E325" : "gray.200" }}
                                                    minW="auto"
                                                    h="auto"
                                                    p={1.5}
                                                    ml={supplierCommunicationCount > 0 ? 2.5 : 0}
                                                    borderRadius="md"
                                                />
                                                {noteCount > 0 && (
                                                    <Flex
                                                        position="absolute"
                                                        top="-4px"
                                                        right="-6px"
                                                        bg="#0071E3"
                                                        color="white"
                                                        borderRadius="full"
                                                        w="16px"
                                                        h="16px"
                                                        align="center"
                                                        justify="center"
                                                        fontSize="9px"
                                                        fontWeight="bold"
                                                        lineHeight="1"
                                                        pointerEvents="none"
                                                    >
                                                        {noteCount}
                                                    </Flex>
                                                )}
                                            </Box>
                                            <CloseButton
                                                size="sm"
                                                fontSize="14px"
                                                color="gray.500"
                                                ml={noteCount > 0 ? 2.5 : 0}
                                                _hover={{ bg: "gray.200", color: "gray.700" }}
                                                onClick={() => {
                                                    if (isChatOpen || isSupplierNotesOpen) {
                                                        setIsChatOpen(false);
                                                        setIsSupplierNotesOpen(false);
                                                    } else {
                                                        onClose();
                                                    }
                                                }}
                                            />
                                        </HStack>
                                    </Flex>

                                    {/* Show More / Order Details + order tags */}
                                    <Flex align="center" mt={3}>
                                        <Flex
                                            align="center"
                                            cursor="pointer"
                                            onClick={onDetailsToggle}
                                            flexShrink={0}
                                            w="108px"
                                        >
                                            <Text fontSize="sm" fontWeight="medium" color="#0071E3">
                                                {isDetailsOpen ? 'Show less' : 'Show more'}
                                            </Text>
                                            <Icon as={(isDetailsOpen ? TbChevronUp : TbChevronDown) as React.ElementType} w={4} h={4} color="#0071E3" ml={1} />
                                        </Flex>
                                        {(data.oemEndorsement || data.dealerReferral || data.potentialTotal || data.quotesOnly || data.reviewedByUsers.length > 0) && (
                                            <Flex align="center" gap={2} ml={3} minW={0} overflowX="auto" overflowY="hidden">
                                                {data.dealerReferral && (
                                                    <Box flexShrink={0}>
                                                        <OrderTag
                                                            tag='dealer-referral'
                                                            text={data.dealerReferral}
                                                        />
                                                    </Box>
                                                )}
                                                {data.oemEndorsement && (
                                                    <Box flexShrink={0}>
                                                        <OrderTag
                                                            tag='oem-endorsement'
                                                            text='OEM Endorsement'
                                                        />
                                                    </Box>
                                                )}
                                                {data.potentialTotal && (
                                                    <Box flexShrink={0}>
                                                        <OrderTag
                                                            tag='potential-total'
                                                            text='Potential Total'
                                                        />
                                                    </Box>
                                                )}
                                                {data.quotesOnly && (
                                                    <Box flexShrink={0}>
                                                        <OrderTag
                                                            tag='quotes-only'
                                                            text='Quotes Only'
                                                        />
                                                    </Box>
                                                )}
                                                {data.reviewedByUsers.map((reviewer) => (
                                                    <Box key={reviewer._id} flexShrink={0}>
                                                        <OrderTag
                                                            tag='reviewed-by'
                                                            text={`Reviewed By ${reviewer.displayName}`}
                                                            onClick={
                                                                sessionContext?.isAuthenticated &&
                                                                    reviewer._id === sessionContext.userId &&
                                                                    orderId
                                                                    ? () => updateOrderDetails.mutate({ orderId, reviewedByMe: false })
                                                                    : undefined
                                                            }
                                                        />
                                                    </Box>
                                                ))}
                                            </Flex>
                                        )}
                                    </Flex>

                                    <Collapse
                                        in={isDetailsOpen}
                                        animateOpacity
                                        style={{
                                            overflow: isDetailsOpen ? 'visible' : 'hidden',
                                            position: 'relative',
                                            zIndex: isDetailsOpen ? 1 : undefined,
                                        }}
                                    >
                                        <Grid templateColumns="repeat(3, 1fr)" gap={4} mt={3} overflow="visible">
                                            <Box overflow="visible">
                                                <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1} letterSpacing="wide" textTransform="uppercase">
                                                    VIN
                                                </Text>
                                                <HStack spacing={2} overflow="visible">
                                                    <CopyTooltip isCopied={isCopied('vin')} zIndex={10}>
                                                        <Text fontSize="sm" fontWeight="medium" color="gray.900">{data.vin}</Text>
                                                    </CopyTooltip>
                                                    <Icon as={TbCopy as React.ElementType} w={4} h={4} color="gray.400" cursor="pointer" _hover={{ color: "gray.600" }} onClick={(e: React.MouseEvent) => { e.stopPropagation(); copyToClipboard(data.vin, 'vin', e); }} />
                                                </HStack>
                                            </Box>

                                            <Box>
                                                <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1} letterSpacing="wide" textTransform="uppercase">
                                                    LOCATION
                                                </Text>
                                                <Text fontSize="sm" fontWeight="medium" color="gray.900">{data.location}</Text>
                                            </Box>

                                            <Box>
                                                <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1} letterSpacing="wide" textTransform="uppercase">
                                                    INSURANCE
                                                </Text>
                                                <HStack spacing={1.5}>
                                                    <Text fontSize="sm" fontWeight="medium" color="gray.900">{data.insurance || '—'}</Text>
                                                    {data.drpJob && (
                                                        <Badge colorScheme="blue" variant="subtle" fontSize="10px">DRP</Badge>
                                                    )}
                                                </HStack>
                                            </Box>

                                            <Box>
                                                <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1} letterSpacing="wide" textTransform="uppercase">
                                                    CUSTOMER
                                                </Text>
                                                <Text fontSize="sm" fontWeight="medium" color="gray.900">{data.customer || '—'}</Text>
                                            </Box>

                                            <Box>
                                                <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1} letterSpacing="wide" textTransform="uppercase">
                                                    CLAIM #
                                                </Text>
                                                <Text fontSize="sm" fontWeight="medium" color="gray.900">{data.claimNumber || '—'}</Text>
                                            </Box>

                                            <Box>
                                                <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1} letterSpacing="wide" textTransform="uppercase">
                                                    MILEAGE
                                                </Text>
                                                <Text fontSize="sm" fontWeight="medium" color="gray.900">
                                                    {data.mileage != null ? data.mileage.toLocaleString() : '—'}
                                                </Text>
                                            </Box>

                                            <Box>
                                                <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1} letterSpacing="wide" textTransform="uppercase">
                                                    DATE CREATED
                                                </Text>
                                                <Text fontSize="sm" fontWeight="medium" color="gray.900">
                                                    {new Date(data.timeCreated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} • {new Date(data.timeCreated).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                                </Text>
                                            </Box>

                                            <Box>
                                                <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1} letterSpacing="wide" textTransform="uppercase">
                                                    DATE LAST UPDATED
                                                </Text>
                                                <Text fontSize="sm" fontWeight="medium" color="gray.900">
                                                    {new Date(data.lastUpdated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} • {new Date(data.lastUpdated).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                                </Text>
                                            </Box>
                                        </Grid>
                                    </Collapse>
                                </Box>

                                {/* Summary Section - White */}
                                <Box px={6} py={3}>
                                    {/* Stat Cards */}
                                    <Grid templateColumns="repeat(4, 1fr)" gap={4} alignItems="stretch">
                                        <GridItem h="100%">
                                            <Flex direction="column" h="100%" bg="white" p={3} borderRadius="lg" borderWidth="1px" borderColor="gray.200" boxShadow="none">
                                                <Text fontSize="xs" color="gray.500" fontWeight="bold" letterSpacing="wide" mb={1} textTransform="uppercase">
                                                    DAYS ACTIVE
                                                </Text>
                                                <Flex flex={1} align="center">
                                                    <Text fontSize="lg" fontWeight="bold" color="gray.900">
                                                        {data.daysActive}
                                                    </Text>
                                                </Flex>
                                            </Flex>
                                        </GridItem>

                                        <GridItem h="100%">
                                            <Flex direction="column" h="100%" bg="white" p={3} borderRadius="lg" borderWidth="1px" borderColor="gray.200" boxShadow="none">
                                                <Text fontSize="xs" color="gray.500" fontWeight="bold" letterSpacing="wide" mb={1} textTransform="uppercase">
                                                    PARTS ORDERED
                                                </Text>
                                                <Flex flex={1} align="center">
                                                    <Text fontSize="lg" fontWeight="bold" color="gray.900">
                                                        {data.partsOrdered}/{data.totalParts}
                                                    </Text>
                                                </Flex>
                                            </Flex>
                                        </GridItem>

                                        <GridItem h="100%">
                                            <Flex direction="column" h="100%" bg="white" p={3} borderRadius="lg" borderWidth="1px" borderColor="gray.200" boxShadow="none">
                                                <Text fontSize="xs" color="gray.500" fontWeight="bold" letterSpacing="wide" mb={1} textTransform="uppercase">
                                                    ORDER VALUE
                                                </Text>
                                                <Flex flex={1} align="center">
                                                    <Text fontSize="lg" fontWeight="bold" color="gray.900">
                                                        {data.orderValue !== null ? formatPrice(data.orderValue) : '-'}
                                                    </Text>
                                                </Flex>
                                            </Flex>
                                        </GridItem>

                                        <GridItem h="100%">
                                            <Flex direction="column" h="100%" bg="white" p={3} borderRadius="lg" borderWidth="1px" borderColor="gray.200" boxShadow="none">
                                                <Text fontSize="xs" color="gray.500" fontWeight="bold" letterSpacing="wide" mb={1} textTransform="uppercase">
                                                    GROSS MARGIN
                                                </Text>
                                                <Box mt="auto">
                                                    <GrossMarginSummaryValue
                                                        grossMargin={data.grossMargin}
                                                        minThreshold={data.minGrossMarginThreshold}
                                                    />
                                                </Box>
                                            </Flex>
                                        </GridItem>
                                    </Grid>

                                    {/* Order Progress - Layered Bar */}
                                    <Box mt={4} bg="white" p={4} borderRadius="lg" borderWidth="1px" borderColor="gray.200" boxShadow="none">
                                        <Flex justify="space-between" align="center" mb={3}>
                                            <Text fontSize="xs" fontWeight="bold" color="gray.600" letterSpacing="wide" textTransform="uppercase">
                                                ORDER PROGRESS
                                            </Text>
                                            <HStack spacing={4}>
                                                {data.partsConfirmed > 0 && (
                                                    <HStack spacing={1.5}>
                                                        <Box w="10px" h="10px" borderRadius="full" bg="#1e3a8a" />
                                                        <Text fontSize="xs" color="gray.600" fontWeight="semibold">{data.partsConfirmed} Confirmed</Text>
                                                    </HStack>
                                                )}
                                                {(data.partsOrdered - data.partsConfirmed) > 0 && (
                                                    <HStack spacing={1.5}>
                                                        <Box w="10px" h="10px" borderRadius="full" bg="#3b82f6" />
                                                        <Text fontSize="xs" color="gray.600" fontWeight="semibold">{data.partsOrdered - data.partsConfirmed} Not Confirmed</Text>
                                                    </HStack>
                                                )}
                                                {(data.totalParts - data.partsOrdered) > 0 && (
                                                    <HStack spacing={1.5}>
                                                        <Box w="10px" h="10px" borderRadius="full" bg="#bfdbfe" />
                                                        <Text fontSize="xs" color="gray.600" fontWeight="semibold">{data.totalParts - data.partsOrdered} Not Ordered</Text>
                                                    </HStack>
                                                )}
                                            </HStack>
                                        </Flex>
                                        <Box position="relative" h="20px" borderRadius="full" overflow="hidden" bg="#bfdbfe">
                                            {/* Ordered layer (medium blue) */}
                                            <Box
                                                position="absolute"
                                                left={0}
                                                top={0}
                                                h="100%"
                                                w={`${(data.partsOrdered / (data.totalParts || 1)) * 100}%`}
                                                bg="#3b82f6"
                                                borderRadius="full"
                                            />
                                            {/* Confirmed layer (dark blue) */}
                                            <Flex
                                                position="absolute"
                                                left={0}
                                                top={0}
                                                h="100%"
                                                w={`${(data.partsConfirmed / (data.totalParts || 1)) * 100}%`}
                                                bg="#1e3a8a"
                                                borderRadius="full"
                                                align="center"
                                                justify="center"
                                                minW="40px"
                                            >
                                                <Text fontSize="xs" fontWeight="bold" color="white">
                                                    {Math.round((data.partsConfirmed / (data.totalParts || 1)) * 100)}%
                                                </Text>
                                            </Flex>
                                        </Box>

                                        {/* Actions Needed - inside progress box */}
                                        {data.actions.length > 0 && !!orderId && (
                                            <Box mt={4} bg="orange.50" p={3} borderRadius="md" borderWidth="1px" borderColor="orange.200">
                                                <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={2} letterSpacing="wide" textTransform="uppercase">
                                                    ACTIONS NEEDED
                                                </Text>
                                                <HStack spacing={3} wrap="wrap">
                                                    {data.actions.map((action) => (
                                                        <ActionBadge
                                                            key={action.key}
                                                            action={action.key}
                                                            total={action.total}
                                                            orderId={orderId}
                                                            source='home'
                                                        />
                                                    ))}
                                                </HStack>
                                            </Box>
                                        )}

                                        {/* Auto-Order Stopped Alerts */}
                                        {data.preOrderBackorder && !data.potentialTotal && !data.quotesOnly && (
                                            <Alert
                                                status="error"
                                                borderRadius="md"
                                                border="1px solid"
                                                borderColor="red.400"
                                                bg="red.50"
                                                mt={4}
                                            >
                                                <AlertIcon color="red.500" />
                                                <AlertDescription color="gray.700" fontSize="sm">
                                                    Auto-ordering was disabled for this pre-order because one or more selected parts are backordered.
                                                </AlertDescription>
                                            </Alert>
                                        )}

                                        {data.potentialTotal && data.quoteRequestAllowedAutoOrder && (
                                            <Alert
                                                status="error"
                                                borderRadius="md"
                                                border="1px solid"
                                                borderColor="red.400"
                                                bg="red.50"
                                                mt={4}
                                            >
                                                <AlertIcon color="red.500" />
                                                <AlertDescription color="gray.700" fontSize="sm">
                                                    Auto-ordering was disabled because the total parts value increased by 50%.
                                                </AlertDescription>
                                            </Alert>
                                        )}
                                    </Box>
                                </Box>
                                <Tabs
                                    variant="unstyled"
                                    display="flex"
                                    flexDirection="column"
                                    flex="1"
                                    overflow="hidden"
                                    index={Math.max(ORDER_DRAWER_WORKFLOW_TABS.indexOf(activeWorkflowTab), 0)}
                                    onChange={(nextIndex) => {
                                        const nextTab = ORDER_DRAWER_WORKFLOW_TABS[nextIndex];
                                        if (nextTab) setActiveWorkflowTab(nextTab);
                                    }}
                                >
                                    <Box px={6} py={2} borderBottomWidth="1px" borderColor="gray.200" flexShrink={0}>
                                        <TabList gap={0} bg="bg.subtle" p="2px" borderRadius="lg">
                                            <Tab {...tabStyles} isDisabled={!hasEms}>
                                                <HStack spacing={1.5}>
                                                    <Icon as={FiFileText as React.ElementType} w="14px" h="14px" />
                                                    <Text>EMS</Text>
                                                </HStack>
                                            </Tab>
                                            <Tab {...tabStyles} isDisabled={!hasQuotes}>
                                                <HStack spacing={1.5}>
                                                    <Icon as={FiDollarSign as React.ElementType} w="14px" h="14px" />
                                                    <Text>Quoting</Text>
                                                    {hasQuotes && data.quotes!.total > 0 && <TabBadge count={data.quotes!.total} />}
                                                </HStack>
                                            </Tab>
                                            <Tab {...tabStyles} isDisabled={!hasSelection}>
                                                <HStack spacing={1.5}>
                                                    <Icon as={FiShoppingCart as React.ElementType} w="14px" h="14px" />
                                                    <Text>Ready to Order</Text>
                                                    {hasSelection && data.selection!.total > 0 && <TabBadge count={data.selection!.total} />}
                                                </HStack>
                                            </Tab>
                                            <Tab {...tabStyles} isDisabled={!hasOrdered}>
                                                <HStack spacing={1.5}>
                                                    <Icon as={FiPackage as React.ElementType} w="14px" h="14px" />
                                                    <Text>Ordered</Text>
                                                    {hasOrdered && data.ordered!.total > 0 && <TabBadge count={data.ordered!.total} />}
                                                </HStack>
                                            </Tab>
                                            <Tab {...tabStyles} isDisabled={!hasRekey}>
                                                <HStack spacing={1.5}>
                                                    <Icon as={FiRefreshCw as React.ElementType} w="14px" h="14px" />
                                                    <Text>Re-key</Text>
                                                </HStack>
                                            </Tab>
                                        </TabList>
                                    </Box>

                                    <Box flex="1" overflow="auto" px={6} py={4} position="relative">
                                        <TabPanels>
                                            <TabPanel px={0} py={0}>
                                                {hasEms && data.ems && (
                                                    <EmsTabPanel
                                                        data={data.ems}
                                                        orderId={orderId || ''}
                                                        onClose={onClose}
                                                    />
                                                )}
                                            </TabPanel>

                                            <TabPanel px={0} py={0}>
                                                {hasQuotes && data.quotes && (
                                                    <QuotingTabPanel data={data.quotes} />
                                                )}
                                            </TabPanel>

                                            <TabPanel px={0} py={0}>
                                                {hasSelection && data.selection && (
                                                    <SelectionTabPanel
                                                        data={data.selection}
                                                        orderId={orderId || ''}
                                                        potentialTotal={data.potentialTotal}
                                                        quotesOnly={data.quotesOnly}
                                                        minGrossMarginThreshold={data.minGrossMarginThreshold}
                                                        drpJob={data.drpJob}
                                                    />
                                                )}
                                            </TabPanel>

                                            <TabPanel px={0} py={0}>
                                                {hasOrdered && data.ordered && (
                                                    <OrderedTabPanel
                                                        data={data.ordered}
                                                        supplierNotes={data.supplierNotes ?? []}
                                                        supplierMessageCounts={data.supplierMessageCounts ?? {}}
                                                        minGrossMarginThreshold={data.minGrossMarginThreshold}
                                                        clickedSupplierOrderId={clickedSupplierOrderId}
                                                        clickedOrderPartId={clickedOrderPartId}
                                                        refetch={refetch}
                                                        onOpenSupplierCommunications={(supplierOrderId, initialTab) => {
                                                            openSupplierCommunications({
                                                                initialTab,
                                                                supplierOrderId,
                                                            });
                                                        }}
                                                    />
                                                )}
                                            </TabPanel>

                                            <TabPanel px={0} py={0}>
                                                {hasRekey && data.rekey && (
                                                    <RekeyTabPanel data={data.rekey} />
                                                )}
                                            </TabPanel>
                                        </TabPanels>
                                    </Box>
                                </Tabs>
                            </Box>

                            {/* Chat Panel (embedded side-by-side) */}
                            {isChatOpen && orderId && (
                                <Box
                                    w="380px"
                                    flexShrink={0}
                                    borderLeftWidth="1px"
                                    borderColor="border.strong"
                                >
                                    <OrderChatPanel
                                        orderId={orderId}
                                        mode="order"
                                        notes={data?.notes ?? []}
                                        initialComposeMode={chatComposeMode}
                                        refetch={refetch}
                                        onClose={() => setIsChatOpen(false)}
                                        embedded
                                    />
                                </Box>
                            )}
                            {isSupplierNotesOpen && (
                                <Box
                                    w="380px"
                                    flexShrink={0}
                                    borderLeftWidth="1px"
                                    borderColor="border.strong"
                                >
                                    <SupplierNotesModal
                                        isOpen={isSupplierNotesOpen}
                                        onClose={() => setIsSupplierNotesOpen(false)}
                                        orderId={orderId ?? undefined}
                                        notes={data?.supplierNotes ?? []}
                                        supplierOrders={supplierOrderSummaries}
                                        initialTab={supplierNotesInitialTab}
                                        initialSupplierOrderId={supplierNotesInitialSupplierOrderId}
                                        embedded
                                    />
                                </Box>
                            )}
                        </Flex>
                    </>
                ) : null}
            </DrawerContent>
        </Drawer>
    );
};

export default OrderDrawer;
